/**
 * /start/account — funnel step 1 (Create Account).
 *
 * Signed out: ONE combined card inside FunnelLayout — the shared Clerk
 * signup engine (pages/SignUp.tsx, review F8 — reused, not duplicated; it
 * mounts the required <div id="clerk-captcha" /> for smart CAPTCHA) extended
 * with the purchase-intent fields (who-for, preferred package, required
 * consent). On submit the intent selections are persisted to sessionStorage
 * (lib/funnel.ts: 'legacyorb.funnel.pendingIntent') BEFORE the Clerk
 * verification round-trip, with redirectTo back here.
 *
 * Signed in: if a pending intent exists in sessionStorage and the server has
 * none yet (the post-verification landing), it is auto-POSTed to
 * /funnel/intent behind a brief "Setting up your account…" spinner — no
 * second form. If the auto-POST fails, or for users who signed up via
 * /sign-up (no stored intent), the classic intent form (spec §3.5) renders:
 * who the account is for, preferred package (preselected from the ?plan=
 * deep link or a previously saved intent), and the required consent checkbox
 * (exact ticket text) → POST /funnel/intent → /start/agreement.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SignUpPage } from '@/pages/SignUp';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { FunnelGuard } from '@/components/funnel/FunnelGuard';
import { useFunnelState, useOfferings, FUNNEL_STATE_QUERY_KEY } from '@/hooks/useFunnel';
import { apiClient } from '@/services/api-client';
import {
  clearPendingFunnelIntent,
  readPendingFunnelIntent,
  readPreferredPlanSlug,
  storePendingFunnelIntent,
  type PendingFunnelIntent,
} from '@/lib/funnel';
import type { ProductOffering, PurchaseIntent, WhoFor } from '@/types';

/** Exact consent text from spec §3.5 — do not edit without a ticket. */
const CONSENT_TEXT =
  'I confirm that I am at least 18 years old and authorized to create this Legacy Orb account. ' +
  'I understand that Legacy Orb will use the information I provide to set up and deliver its ' +
  'memory preservation services. If this account is for someone else, I confirm that I will ' +
  'obtain any necessary consent before submitting their personal stories, photos, voice, or ' +
  'family information. I understand that I will review and accept the Service Agreement and ' +
  'Privacy Policy on the next step.';

const WHO_FOR_OPTIONS: Array<{ value: WhoFor; label: string }> = [
  { value: 'myself', label: 'Myself' },
  { value: 'parent_grandparent', label: 'Parent or grandparent' },
  { value: 'gift', label: 'Gift for family member' },
];

const NO_PREFERENCE = 'none';

function postIntent(intent: { whoFor: WhoFor; preferredOfferingSlug: string | null }) {
  return apiClient.post<PurchaseIntent>('/api/v1/funnel/intent', {
    whoFor: intent.whoFor,
    ...(intent.preferredOfferingSlug
      ? { preferredOfferingSlug: intent.preferredOfferingSlug }
      : {}),
    consentAccepted: true,
  });
}

export function FunnelAccountPage() {
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSignedIn) {
    // Step 1 — one combined card: account creation + purchase intent.
    return <FunnelSignUp />;
  }

  // Signed in — auto-submit a pending intent, or show the intent form.
  return (
    <FunnelGuard step="account">
      <FunnelLayout step={1} width="2xl">
        <SignedInAccountStep />
      </FunnelLayout>
    </FunnelGuard>
  );
}

/**
 * Signed-out step 1: the shared Clerk signup card (funnel variant) with the
 * intent fields injected via renderExtraFields. onBeforeSubmit validates the
 * intent selections and persists them to sessionStorage before signUp.create
 * runs, so they survive the e-mail verification round-trip.
 */
function FunnelSignUp() {
  const { data: offerings, isLoading: offeringsLoading } = useOfferings();

  const [whoFor, setWhoFor] = useState<WhoFor | null>(null);
  const [preferredSlug, setPreferredSlug] = useState<string>(NO_PREFERENCE);
  const [consentChecked, setConsentChecked] = useState(false);
  const [intentError, setIntentError] = useState('');

  // Preselect the package from the ?plan= deep link, exactly once (review
  // F7 — refetch-driven effect re-runs must not overwrite "Choose later").
  const planPrefillDone = useRef(false);
  useEffect(() => {
    if (planPrefillDone.current || offeringsLoading) return;
    planPrefillDone.current = true;
    const slug = readPreferredPlanSlug();
    if (slug && offerings?.some((o) => o.slug === slug)) {
      setPreferredSlug((current) => (current === NO_PREFERENCE ? slug : current));
    }
  }, [offerings, offeringsLoading]);

  const handleBeforeSubmit = () => {
    if (!whoFor) {
      setIntentError('Please select who this account is for.');
      return false;
    }
    if (!consentChecked) {
      setIntentError('Please accept the consent statement to continue.');
      return false;
    }
    setIntentError('');
    // Persist FIRST — the Clerk e-mail verification may involve a refresh or
    // a return visit, and the selections must survive it.
    storePendingFunnelIntent({
      whoFor,
      preferredOfferingSlug: preferredSlug !== NO_PREFERENCE ? preferredSlug : null,
      consentAccepted: true,
    });
    return true;
  };

  return (
    <FunnelLayout step={1} width="2xl">
      <SignUpPage
        variant="funnel"
        redirectTo="/start/account"
        submitDisabled={!whoFor || !consentChecked}
        onBeforeSubmit={handleBeforeSubmit}
        renderExtraFields={({ isSubmitting }) => (
          <>
            <IntentFields
              whoFor={whoFor}
              onWhoForChange={setWhoFor}
              preferredSlug={preferredSlug}
              onPreferredSlugChange={setPreferredSlug}
              consentChecked={consentChecked}
              onConsentChange={setConsentChecked}
              offerings={offerings}
              offeringsLoading={offeringsLoading}
              disabled={isSubmitting}
            />
            {intentError && <p className="text-sm text-destructive">{intentError}</p>}
          </>
        )}
      />
    </FunnelLayout>
  );
}

/**
 * Signed-in step 1. When the user lands here right after e-mail verification
 * (pending intent in sessionStorage, none on the server), the intent is
 * auto-POSTed behind a spinner and the user moves straight to the agreement.
 * Otherwise (signed up via /sign-up, or the auto-POST failed) the intent
 * form renders — prefilled from the stored values after a failure.
 */
function SignedInAccountStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Loaded — FunnelGuard only renders children once the state query settles.
  const { data: funnelState } = useFunnelState();

  const [pendingIntent] = useState(() => readPendingFunnelIntent());
  const [phase, setPhase] = useState<'auto' | 'form'>(() =>
    pendingIntent && !funnelState?.intent ? 'auto' : 'form'
  );
  const [autoError, setAutoError] = useState('');
  const autoSubmitStarted = useRef(false);

  useEffect(() => {
    if (phase !== 'auto' || !pendingIntent || autoSubmitStarted.current) return;
    autoSubmitStarted.current = true;
    void (async () => {
      const response = await postIntent(pendingIntent);
      // One shot either way — on failure the form prefills from memory.
      clearPendingFunnelIntent();
      if (!response.success) {
        setAutoError(response.error?.message || 'Failed to save your details. Please try again.');
        setPhase('form');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY });
      navigate('/start/agreement');
    })();
  }, [phase, pendingIntent, navigate, queryClient]);

  // Stale leftover (the server already has an intent) — discard it so a
  // back-navigation visit doesn't re-submit old selections later.
  useEffect(() => {
    if (phase === 'form' && pendingIntent && funnelState?.intent) {
      clearPendingFunnelIntent();
    }
  }, [phase, pendingIntent, funnelState]);

  if (phase === 'auto') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Setting up your account…</p>
      </div>
    );
  }

  return (
    <IntentForm
      initialValues={autoError ? (pendingIntent ?? undefined) : undefined}
      initialError={autoError}
    />
  );
}

interface IntentFormProps {
  /** Prefill after a failed auto-submit of a stored pending intent. */
  initialValues?: PendingFunnelIntent;
  initialError?: string;
}

function IntentForm({ initialValues, initialError = '' }: IntentFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: funnelState, isLoading: funnelStateLoading } = useFunnelState();
  const { data: offerings, isLoading: offeringsLoading } = useOfferings();

  const [whoFor, setWhoFor] = useState<WhoFor | null>(initialValues?.whoFor ?? null);
  const [preferredSlug, setPreferredSlug] = useState<string>(
    initialValues?.preferredOfferingSlug ?? NO_PREFERENCE
  );
  const [consentChecked, setConsentChecked] = useState(initialValues?.consentAccepted ?? false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(initialError);

  // Prefill from a previously saved intent (resume) or the ?plan= deep link.
  // The plan-slug prefill runs exactly once (review F7): window-refocus
  // refetches re-fire this effect, and without the guard they would
  // overwrite a user's explicit "Choose later" selection.
  const planPrefillDone = useRef(false);
  useEffect(() => {
    const intent = funnelState?.intent;
    if (intent) {
      setWhoFor((current) => current ?? intent.whoFor);
    }
    if (planPrefillDone.current || funnelStateLoading || offeringsLoading) return;
    planPrefillDone.current = true;
    const slug = funnelState?.intent?.preferredOfferingSlug ?? readPreferredPlanSlug();
    if (slug && offerings?.some((o) => o.slug === slug)) {
      setPreferredSlug((current) => (current === NO_PREFERENCE ? slug : current));
    }
  }, [funnelState, offerings, funnelStateLoading, offeringsLoading]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!whoFor || !consentChecked) return;

    setError('');
    setIsSubmitting(true);

    const response = await postIntent({
      whoFor,
      preferredOfferingSlug: preferredSlug !== NO_PREFERENCE ? preferredSlug : null,
    });

    setIsSubmitting(false);

    if (!response.success) {
      setError(response.error?.message || 'Failed to save your details. Please try again.');
      return;
    }

    await queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY });
    navigate('/start/agreement');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-2xl">Tell us about this account</CardTitle>
        <CardDescription>
          A few details so we can set up your Legacy Orb experience.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <IntentFields
            whoFor={whoFor}
            onWhoForChange={setWhoFor}
            preferredSlug={preferredSlug}
            onPreferredSlugChange={setPreferredSlug}
            consentChecked={consentChecked}
            onConsentChange={setConsentChecked}
            offerings={offerings}
            offeringsLoading={offeringsLoading}
            disabled={isSubmitting}
          />

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!whoFor || !consentChecked || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue to Agreement
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

interface IntentFieldsProps {
  whoFor: WhoFor | null;
  onWhoForChange: (value: WhoFor) => void;
  preferredSlug: string;
  onPreferredSlugChange: (value: string) => void;
  consentChecked: boolean;
  onConsentChange: (value: boolean) => void;
  offerings: ProductOffering[] | undefined;
  offeringsLoading: boolean;
  disabled?: boolean;
}

/**
 * The purchase-intent fields (spec §3.5), shared between the combined
 * signed-out signup card and the signed-in intent form.
 */
function IntentFields({
  whoFor,
  onWhoForChange,
  preferredSlug,
  onPreferredSlugChange,
  consentChecked,
  onConsentChange,
  offerings,
  offeringsLoading,
  disabled = false,
}: IntentFieldsProps) {
  return (
    <>
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-foreground">Who is this for?</legend>
        {WHO_FOR_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 transition-colors hover:bg-muted/50 has-[:checked]:border-primary"
          >
            <input
              type="radio"
              name="whoFor"
              value={option.value}
              checked={whoFor === option.value}
              onChange={() => onWhoForChange(option.value)}
              className="h-4 w-4 accent-primary"
              required
              disabled={disabled}
            />
            <span className="text-sm text-foreground">{option.label}</span>
          </label>
        ))}
      </fieldset>

      <div className="space-y-2">
        <Label>Preferred package (optional)</Label>
        {offeringsLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading packages...
          </div>
        ) : (
          <Select value={preferredSlug} onValueChange={onPreferredSlugChange} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Choose later" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PREFERENCE}>Choose later</SelectItem>
              {/* Coming-soon plans are excluded — the product step would
                  refuse to preselect them anyway (86bar0jc1). */}
              {(offerings ?? [])
                .filter((offering) => offering.status !== 'coming_soon')
                .map((offering) => (
                  <SelectItem key={offering.id} value={offering.slug}>
                    {offering.name} — {offering.planLabel}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
        <p className="text-xs text-muted-foreground">
          You can change your selection on the product step.
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-4">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => onConsentChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          required
          disabled={disabled}
        />
        <span className="text-sm leading-relaxed text-muted-foreground">{CONSENT_TEXT}</span>
      </label>
    </>
  );
}
